import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyValidateAddressQuery } from '../../services/api';
import { setSelectedAddressLocation } from '../../store/actions/general';
import { filterSelector } from '../../store/selectors/filters';
import { selectedAddressLocationSelector } from '../../store/selectors/general';
import { toast } from 'react-toastify';
import { trackEvent, MAP_ADDRESS_SEARCH } from '../../utils/googleAnalytics';

export const AddressSearch = ({ onValidatingChange, isDesktop = true }) => {
  const { current: map } = useMap();
  const dispatch = useDispatch();
  const selectedAddressLocation = useSelector(selectedAddressLocationSelector);
  const filterState = useSelector(filterSelector);
  const [validateAddressTrigger] = useLazyValidateAddressQuery();
  const [isValidating, setIsValidating] = useState(false);

  // when you set state, also notify parent
  const setValidating = val => {
    setIsValidating(val);
    onValidatingChange?.(val);
  };

  // Keep a ref to the geocoder instance so we can call setInput outside the effect that creates it
  const geocoderRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    const geocoder = new MapboxGeocoder({
      accessToken: process.env.REACT_APP_MAPBOX_KEY,
      mapboxgl,
      countries: 'us',
      marker: false,
      flyTo: false,
      placeholder: 'Search for an address',
    });

    geocoderRef.current = geocoder;

    const handleResult = async ({ result }) => {
      const [lon, lat] = result.center;
      const label = result.place_name;
      // const county = result.context
      //   ?.find(item => item.id.startsWith('district'))
      //   ?.text.replace(' County', '');

      trackEvent(MAP_ADDRESS_SEARCH, { lat: lat, lon: lon });

      setValidating(true);
      try {
        const result = await validateAddressTrigger({ lat, lon });

        if (result.error) {
          toast.error('We were unable to validate that address. Please try again later.');
          console.error('Error validating address:', result.error);
          return;
        }

        if (result.data && !result.data?.isValid) {
          geocoderRef.current?.clear();
          toast.error('Address must be within the study area.');
          return;
        } else {
          dispatch(setSelectedAddressLocation({ lat, lon, label, county: result.data.county }));
          map.flyTo({
            center: [lon, lat],
            zoom: 16,
            duration: 2000,
          });
        }
      } finally {
        setValidating(false);
      }
    };

    const handleClear = () => {
      dispatch(setSelectedAddressLocation(null));
    };

    geocoder.on('result', handleResult);
    geocoder.on('clear', handleClear);

    const geocoderContainer = document.createElement('div');
    geocoderContainer.className =
      'absolute top-4 left-1/2 -translate-x-1/2 z-[1000] w-[18rem] lg:w-[18rem] 2xl:w-96 max-w-[calc(100vw-2rem)]';
    const geocoderEl = geocoder.onAdd(map);
    geocoderContainer.appendChild(geocoderEl);
    map.getContainer().appendChild(geocoderContainer);

    // If there's already a selected address in Redux when the component mounts populate the search bar
    if (selectedAddressLocation?.label) {
      geocoder.setInput(selectedAddressLocation.label);
    }

    return () => {
      geocoder.off('result', handleResult);
      geocoder.off('clear', handleClear);
      geocoderRef.current = null;
      if (map.getContainer().contains(geocoderContainer)) {
        map.getContainer().removeChild(geocoderContainer);
      }
    };
    // eslint-disable-next-line
  }, [map, isDesktop]);

  // Sync label into search bar if selectedAddressLocation changes
  useEffect(() => {
    if (!geocoderRef.current) return;
    if (selectedAddressLocation?.label) {
      geocoderRef.current.setInput(selectedAddressLocation.label);
    } else {
      geocoderRef.current.clear();
    }
  }, [selectedAddressLocation]);

  useEffect(() => {
    if (!filterState.countySelectedFromAddress && geocoderRef.current) {
      geocoderRef.current.clear();
    }
    // eslint-disable-next-line
  }, [filterState.countySelectedFromAddress, geocoderRef.current, map]);

  return (
    <>
      {isValidating && (
        <div className="pointer-events-none absolute inset-0 z-[9999] flex items-center justify-center bg-white bg-opacity-70">
          <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-teal" />
          <p className="ml-4 text-lg font-medium">Validating address...</p>
        </div>
      )}
    </>
  );
};
